/**
 * wayai relabel <label> [--hub <id|name>] — set a preview hub's label
 * wayai relabel --clear [--hub <id|name>] — remove the label
 *
 * `preview_label` is server-owned, preview-only metadata that disambiguates
 * sibling previews (the `<hub>--<label>` folder slug). It is NOT part of the
 * declarative config — editing it in hub.yaml and running `wayai push` is ignored.
 * This command is the imperative way to change it, mirroring the platform UI.
 *
 * Targets a single preview hub (production is read-only and has no label):
 *   - --hub <uuid|folder-name> → that hub
 *   - cwd inside a hub folder → that hub
 *   - exactly one preview in the hubs dir → it
 */

import * as path from 'node:path';
import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { setPreviewLabelInHubYaml } from '../lib/yaml-writer.js';
import { selectExistingHub } from './push.js';
import {
  autoRenameHubFolder,
  filterPreviewHubs,
  findGitRoot,
  resolveWorkspaceDir,
  scanWorkspaceHubs,
} from '../lib/workspace.js';
import { warnLayoutOnce, hubsDirLabel } from '../lib/layout.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { assertInScope } from '../lib/worktree-scope.js';
import { PREVIEW_LABEL_MAX_LENGTH } from '@wayai/core/contracts';

interface ParsedArgs {
  label?: string;
  clear: boolean;
  hubSelector?: string;
}

function parseArgs(args: string[]): ParsedArgs {
  let label: string | undefined;
  let clear = false;
  let hubSelector: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--clear') {
      clear = true;
    } else if (arg === '--hub' && args[i + 1]) {
      hubSelector = args[++i];
    } else if (!arg.startsWith('-') && label === undefined) {
      label = arg;
    }
  }

  return { label, clear, hubSelector };
}

export async function relabelCommand(args: string[]): Promise<void> {
  const { label, clear, hubSelector } = parseArgs(args);

  if (!clear && (label === undefined || label.trim() === '')) {
    console.error('Usage: wayai relabel <label> [--hub <uuid|folder>]   (or --clear to remove the label)');
    process.exit(1);
  }

  // Normalize client-side to match the backend transform (`trim() || null`), so the
  // value we use for the local folder rename equals what the server stored.
  const normalizedLabel = clear ? null : (label as string).trim();
  if (normalizedLabel && normalizedLabel.length > PREVIEW_LABEL_MAX_LENGTH) {
    console.error(`preview_label must be ${PREVIEW_LABEL_MAX_LENGTH} characters or fewer.`);
    process.exit(1);
  }

  requireRepoConfig(); // ensure this repo is scoped to an org before any network call

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const workspaceDir = resolveWorkspaceDir();
  const gitRoot = findGitRoot();
  const wsLabel = hubsDirLabel(gitRoot);
  if (gitRoot) warnLayoutOnce(gitRoot);

  // Reuse push's resolver (--hub | enclosing | sole preview). It returns null on
  // 0 or >1 previews without a selector — relabel errors there (unlike push, which
  // falls through to new-hub creation).
  const allHubs = scanWorkspaceHubs(workspaceDir);
  const target = selectExistingHub(workspaceDir, allHubs, hubSelector, wsLabel);
  if (!target) {
    const previewHubs = filterPreviewHubs(allHubs);
    if (previewHubs.length === 0) {
      console.error(`No preview hub found in ${wsLabel}/. Run \`wayai pull\` first, or pass --hub <uuid|folder-name>.`);
    } else {
      console.error(`Multiple hubs found in ${wsLabel}/. Pass --hub <uuid|folder-name> to choose, or run from inside a hub folder:`);
      for (const h of previewHubs) {
        console.error(`  ${path.basename(h.hubFolder)} (${h.hubId})`);
      }
    }
    process.exit(1);
  }

  if (target.hubEnvironment === 'production') {
    console.log('This is a read-only production mirror — production hubs have no preview label. Relabel the linked preview hub instead.');
    return;
  }
  assertInScope('hubs', target.hubId, path.basename(target.hubFolder));

  console.log(normalizedLabel ? `Setting preview label to "${normalizedLabel}"...` : 'Clearing preview label...');

  // The PATCH returns the full updated hub row (SELECT *), so it already carries
  // the normalized label + branch_name + name needed for the local hub.yaml edit
  // and folder rename — no second round-trip, no full-config download. Only the
  // echoed label is touched, so any unpushed local edits to other fields survive.
  const { data } = await client.relabelPreview(target.hubId, normalizedLabel);
  const row = data[0];
  const serverLabel = row.preview_label ?? null;

  setPreviewLabelInHubYaml(target.hubFolder, serverLabel);
  const finalFolder = autoRenameHubFolder(
    target.hubFolder,
    row.hub_name,
    row.hub_environment,
    row.hub_id,
    serverLabel,
    row.branch_name ?? null,
  );

  console.log(serverLabel ? `Preview label set to "${serverLabel}".` : 'Preview label cleared.');
  // autoRenameHubFolder already logs the rename + the git-stage note on success;
  // just confirm the final folder (mirrors push.ts's syncAfterPush, no duplicate note).
  if (finalFolder !== target.hubFolder) {
    console.log(`Hub folder is now ${path.basename(finalFolder)}`);
  }
}
